/** A player's preferred name, from InLeague's `playerNickName` (#854).
 *
 *  ⚠️ THE NICKNAME FIELD IS OFTEN JUNK. Parents copy the first or full name
 *  into it (21 of 438 Fall 2026 B7/B10 players, 2026-10-14), and showing
 *  "John (John) Smith" is worse than showing nothing. This is the one rule that
 *  decides; ingest stores its answer, so no reader applies it again. #742
 *  (volunteer nicknames) reuses it. */

const squash = (s: string): string => s.trim().replace(/\s+/g, ' ')
const fold = (s: string): string => squash(s).toLowerCase()

/** The trimmed nickname, or null when it is blank or only repeats the first
 *  name or `first last` (case- and whitespace-insensitive). */
export function preferredName(first: string, last: string, nickname: string | null | undefined): string | null {
  const nick = squash(nickname ?? '')
  if (nick === '') return null
  const n = nick.toLowerCase()
  if (n === fold(first) || n === fold(`${first} ${last}`)) return null
  return nick
}

/** `John (Jack) Smith`, or `John Smith` with no preferred name. The game card
 *  and the stats review roster both print this, so the reviewer reads the same
 *  words the coach's card carried. */
export function nameWithPreferred(first: string, preferred: string | null | undefined, last: string): string {
  const p = squash(preferred ?? '')
  return [squash(first), p === '' ? '' : `(${p})`, squash(last)].filter((s) => s !== '').join(' ')
}

/** Roman-numeral name suffixes kept upper case after tidying. */
const ROMAN_SUFFIX = /^(ii|iii|iv)$/i

/** A player name typed in ONE case, title-cased; anything else unchanged.
 *
 *  ⚠️ MIXED CASE IS NEVER TOUCHED. "McDonald", "DeAndre" and "LaToya" carry
 *  capitals nobody can reconstruct, so a name with both upper and lower
 *  letters comes back exactly as typed. Only all-lower ("john smith") and
 *  all-caps ("JOHN SMITH") are rewritten. The cost: "mcdonald" becomes
 *  "Mcdonald".
 *
 *  A word starts after a space, hyphen or apostrophe ("Mary-Kate O'Brien").
 *  A roman suffix after the first word stays upper ("Smith III"). A name that
 *  is just two capitals ("AJ") is initials and stays as typed. */
export function tidyNameCase(name: string): string {
  const hasUpper = /\p{Lu}/u.test(name)
  const hasLower = /\p{Ll}/u.test(name)
  if (hasUpper === hasLower) return name
  if (hasUpper && /^\s*\p{Lu}{2}\s*$/u.test(name)) return name
  const titled = name.toLowerCase().replace(/(^|[\s\-'’])(\p{Ll})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase())
  return titled.split(' ').map((w, i) => (i > 0 && ROMAN_SUFFIX.test(w) ? w.toUpperCase() : w)).join(' ')
}
